import React from 'react';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { Link } from 'react-router-dom';
import { media, layout, styledUtils } from '@prisjakt/ui-base';
import { colors, remCalc } from '@prisjakt/ui-base/styles/variables';
import RatingComponent from '@prisjakt/ui-rating';
import Button from '@prisjakt/ui-button';
import ProductCheckbox, {
  productCheckboxHoverStyle,
} from '@prisjakt/ui-product-checkbox';

export const Card = styled.div.attrs({ 'data-test': 'ProductCard' })`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 0 50%;
  align-self: stretch;
  width: 100%;
  min-height: 100%;
  min-width: ${layout.productCardMinWidthMobile};
  max-width: ${layout.productCardMaxWidth};
  text-align: ${ifProp('textCenter', 'center', 'left')};
  border-bottom: ${p =>
    p.hasDivider ? `1px solid ${p.theme.lines.primary}` : 'none'};

  ${media.mediumUp`
    flex: 1 0 ${layout.productCardMinWidth};
  `};
`;

const smallImageProductCardPadding = '1rem';

const smallImageTestLink = css`
  padding: ${smallImageProductCardPadding};
`;
const largeImageTestLink = css`
  padding: ${layout.productCardPaddingSmall};
  ${media.mediumUp`
    padding: ${layout.productCardPaddingLarge};
  `}
`;
export const ProductLink = styled(({ showLargerImage, ...linkProps }) => (
  <Link {...linkProps} />
))`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  position: relative;
  outline: none;
  ${({ showLargerImage }) =>
    showLargerImage ? largeImageTestLink : smallImageTestLink}

  ${props =>
    props.selected &&
    css`
      &::before {
        ${styledUtils.overlay(props.theme.bg.productCompareLight)};
      }
    `}

  @media (pointer: fine) {
    &:hover::after,
    &.focus-visible::after {
      ${styledUtils.overlay()}
    }
    ${/* sc-selector */ Card}:focus-within &::after {
      ${styledUtils.overlay()}
    }
  }
`;

export const CampaignBadge = styled.span`
  max-height: 55px;
  max-width: 55px;
  position: absolute;
  top: 0;
  left: 0;
  ${({ showLargerImage }) =>
    showLargerImage ? largeImageTestLink : smallImageTestLink}
`;

const productImageSmall = css`
  height: 90px;
  width: 90px;
  margin: 10px auto 20px auto;
  ${media.mediumUp`
    height: 130px;
    width: 130px;
  `};
`;
const productImageLarge = css`
  height: ${layout.productCardImageSize};
  width: ${layout.productCardImageSize};
  margin: 0 auto 20px auto;
  img {
    max-height: 100%;
  }
`;
export const ProductImage = styled.div`
  align-items: center;
  box-sizing: content-box;
  order: 0;
  text-align: center;
  height: ${layout.productCardImageSize};
  width: ${layout.productCardImageSize};
  margin: 0 auto 20px auto;
  background: ${colors.lightGray1};
  border-radius: ${layout.borderRadius};
  ${({ showLargerImage }) =>
    showLargerImage ? productImageLarge : productImageSmall}

  img {
    max-height: 100%;
  }
`;

export const BrandLogo = styled.span`
  max-height: 55px;
  max-width: 55px;
  position: absolute;
  top: 0;
  right: 0;
  padding: 1rem;
`;

const productDetailsImageLarge = css`
  padding: 0 ${layout.spacing.xxSmall};
  ${media.mediumUp`
  padding: 0;
`}
`;
export const ProductDetails = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: ${ifProp('stretch', '1 0 auto', '0 0 auto')};
  ${({ showLargerImage }) => showLargerImage && productDetailsImageLarge}
`;

export const Rating = styled(RatingComponent)`
  position: absolute;
  bottom: 100%;
  padding: 2px;
  align-self: center;
  background: ${colors.white};
`;

const productNameImageSmall = css`
  margin-top: 5px;
`;
const productNameImageLarge = css`
  ${media.mediumUp`
    margin-top: ${layout.spacing.xxSmall};
  `}
`;

export const ProductName = styled.div.attrs({ 'data-test': 'ProductName' })`
  overflow-wrap: break-word;
  ${({ showLargerImage }) =>
    showLargerImage ? productNameImageLarge : productNameImageSmall}
`;

export const Subtitle = styled.div`
  color: ${p => p.theme.text.tertiary};
  font-size: ${remCalc(13)};

  ${media.mediumUp`
    font-size: ${remCalc(12)};
  `};
`;

export const ExtraFooter = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  font-size: 13px;
  color: ${p => p.theme.text.tertiary};

  ${media.mediumUp`
    font-size: 12px;
  `};
`;

export const Footer = styled.div`
  display: block;
  position: relative;
  outline: none;

  @media (pointer: fine) {
    ${/* sc-selector */ ProductLink}:hover + &::after,
    ${/* sc-selector */ ProductLink}:focus + &::after {
      ${styledUtils.overlay()}
    }
  }
`;

export const CategoryArea = styled.div`
  display: block;
  padding: 0 1rem 1rem;
  position: relative;
  outline: none;

  @media (pointer: fine) {
    ${/* sc-selector */ ProductLink}:hover + &::after,
    ${/* sc-selector */ ProductLink}:focus + &::after {
      ${styledUtils.overlay()}
    }
  }
`;

export const CategoryLink = styled(Button)`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  box-sizing: border-box;

  && {
    text-transform: none;
  }

  &:hover,
  &:focus {
    color: ${p => p.theme.text.callToActionHover};
  }
`;

const diffPercentageImageSmall = css`
  top: ${smallImageProductCardPadding};
  left: ${smallImageProductCardPadding};
`;
const diffPercentageImageLarge = css`
  top: ${layout.productCardPaddingSmall};
  left: ${layout.productCardPaddingSmall};
  ${media.mediumUp`
    top: ${layout.productCardPaddingLarge};
    left: ${layout.productCardPaddingLarge};
  `}
`;
export const DiffPercentage = styled.div`
  background: ${colors.green1};
  position: absolute;
  padding: 0.1rem 0.4rem;
  font-weight: 600;
  color: ${colors.white};
  border-radius: ${layout.borderRadius};
  ${({ showLargerImage }) =>
    showLargerImage ? diffPercentageImageLarge : diffPercentageImageSmall}
`;

export const StoreLogo = styled.div`
  margin-left: 10px;
  max-width: 40%;
`;

const priceInfoImageSmall = css`
  margin-top: 6px;
`;
const priceInfoImageLarge = css`
  padding: 0 ${layout.spacing.xxSmall};
  ${media.mediumUp`
    padding: 0;
    margin-top: ${layout.spacing.xxSmall};
  `}
`;

export const PriceInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${({ showLargerImage }) =>
    showLargerImage ? priceInfoImageLarge : priceInfoImageSmall}
`;

export const Checkbox = styled(ProductCheckbox).attrs(() => ({
  vertical: false,
}))`
  position: absolute;
  top: 10px;
  left: 10px;

  ${/* sc-selector */ Card}:hover & {
    ${productCheckboxHoverStyle}
  }
`;
